import classNames from "classnames";

import {
  DEFAULT_FILTER_TYPE,
  GT_LT_GTE_LTE_OPERATORS,
} from "features/ui/Filters/constants";
import { FilterGroupState } from "features/ui/Filters/FilterBuilder/types";
import { getTopLevelRowFromFilterGroupState } from "features/ui/Filters/FilterBuilder/utils";
import ContainsStartsWithFilter from "features/ui/Filters/FilterTypes/ContainsStartsWithFilter";
import DateFilters from "features/ui/Filters/FilterTypes/DateFilters";
import DurationFilter from "features/ui/Filters/FilterTypes/DurationFilter";
import ExistsFilter from "features/ui/Filters/FilterTypes/ExistsFilter/ExistsFilter";
import NumberFilters from "features/ui/Filters/FilterTypes/NumberFilters";
import OccursFilter from "features/ui/Filters/FilterTypes/OccursFilter/OccursFilter";
import SelectFilter from "features/ui/Filters/FilterTypes/SelectFilter";
import { FilterOperator, FilterSchemaItem } from "features/ui/Filters/types";
import { prepareFilterValuesForAPI } from "features/ui/Filters/utils";
import { SchemaEntry } from "features/ui/MaterialTable";
import { SelectOption } from "features/ui/Select";

import MultipleSelectFilter from "./SelectFilter/MultipleSelectFilter";

interface Props {
  isActiveFilter: boolean;
  selectedOperatorId: FilterOperator;
  currentFilterSchema: FilterSchemaItem;
  filters?: FilterGroupState;
  onValuesChanged: (values: string[]) => void;
  initialized?: boolean;
  className?: string;
  disabled?: boolean;
  isAdvancedFilterEditor?: boolean;
  autoFocus?: boolean;
  occursFilterSelector?: boolean;
  fullWidth?: boolean;
  isInsideFilterBuilder?: boolean;
  searchIcon?: boolean;
  placeholder?: string;
  multiSelect?: boolean;
  defaultLoadLimit?: number;
  limitTags?: number;
  relationSchema?: SchemaEntry[];
}

const FilterTypeWrap = ({
  selectedOperatorId,
  currentFilterSchema,
  filters,
  onValuesChanged,
  className,
  disabled,
  isAdvancedFilterEditor,
  autoFocus,
  occursFilterSelector,
  fullWidth,
  isInsideFilterBuilder,
  searchIcon,
  placeholder,
  multiSelect,
  defaultLoadLimit,
  limitTags,
  relationSchema,
  ...otherProps
}: Props) => {
  const {
    filterType = DEFAULT_FILTER_TYPE,
    fieldName,
    onlyAllowPositiveIntegers,
    filterDataType,
    relationEndpoint,
    baseEntityText,
    ...otherFilterSchemaProps
  } = currentFilterSchema;

  const childRow = getTopLevelRowFromFilterGroupState(fieldName, filters);

  const currentFilterValues: string[] = (childRow && childRow.values) || [];

  const selectedValuesOptions: SelectOption[] = currentFilterValues.map(
    (s) => ({ id: s, value: s })
  );

  const inputValue1 = currentFilterValues[0] || "";
  const inputValue2 = currentFilterValues[1] || "";

  const handleOnValuesChange = (values: string[]) => {
    onValuesChanged(prepareFilterValuesForAPI(values, selectedOperatorId));
  };

  const isAdvancedMultiRowFilter =
    isAdvancedFilterEditor &&
    [
      FilterOperator.EXISTS,
      FilterOperator.NOT_EXISTS,
      FilterOperator.OCCURS,
      FilterOperator.NOT_OCCURS,
    ].includes(selectedOperatorId);

  return (
    <>
      <div
        className={classNames("min-w-20", className, {
          "basis-full order-4": isAdvancedMultiRowFilter,
          "ml-8": occursFilterSelector,
        })}
        data-testid={`filter-type-wrap-${fieldName}`}
      >
        {/* Multi-select defaults to the checkbox dropdown; the filter builder
            deliberately keeps the plain autocomplete. */}
        {[FilterOperator.IN, FilterOperator.NOT_IN].includes(
          selectedOperatorId
        ) &&
          (multiSelect && !isInsideFilterBuilder ? (
            <MultipleSelectFilter
              autoFocus={autoFocus}
              key={`${fieldName}-${selectedOperatorId}`}
              initialSelected={selectedValuesOptions}
              fieldName={fieldName}
              onChange={handleOnValuesChange}
              filterType={filterType}
              filterDataType={filterDataType}
              disabled={disabled}
              fullWidth={fullWidth}
              {...otherProps}
              {...otherFilterSchemaProps}
              loadDataOnOpen={true}
              placeholder={placeholder}
              defaultLoadLimit={defaultLoadLimit}
              limitTags={limitTags}
              label=""
            />
          ) : (
            <SelectFilter
              autoFocus={autoFocus}
              key={`${fieldName}-${selectedOperatorId}`}
              initialSelected={selectedValuesOptions}
              fieldName={fieldName}
              onChange={handleOnValuesChange}
              filterType={filterType}
              filterDataType={filterDataType}
              disabled={disabled}
              fullWidth={fullWidth}
              {...otherProps}
              {...otherFilterSchemaProps}
              multiple={multiSelect}
              loadDataOnOpen={true}
              isInsideFilterBuilder={isInsideFilterBuilder}
              placeholder={placeholder}
              defaultLoadLimit={defaultLoadLimit}
              limitTags={limitTags}
              label=""
            />
          ))}
        {relationEndpoint &&
          [FilterOperator.EXISTS, FilterOperator.NOT_EXISTS].includes(
            selectedOperatorId
          ) && (
            <ExistsFilter
              key={`${fieldName}-${selectedOperatorId}`}
              fieldName={fieldName}
              onChange={handleOnValuesChange}
              disabled={disabled}
              currentFilterValues={currentFilterValues}
              relationEndpoint={relationEndpoint}
              relationSchema={relationSchema}
              {...otherProps}
              {...otherFilterSchemaProps}
            />
          )}
        {[FilterOperator.OCCURS, FilterOperator.NOT_OCCURS].includes(
          selectedOperatorId
        ) && (
          <OccursFilter
            autoFocus={autoFocus}
            key={`${fieldName}-${selectedOperatorId}`}
            fieldName={fieldName}
            onChange={handleOnValuesChange}
            disabled={disabled}
            currentFilterValues={currentFilterValues}
            isAdvancedFilterEditor={isAdvancedFilterEditor}
            baseEntityText={baseEntityText}
            {...otherProps}
            {...otherFilterSchemaProps}
          />
        )}
        {[
          FilterOperator.CONTAINS,
          FilterOperator.NOT_CONTAINS,
          FilterOperator.STARTS_WITH,
          FilterOperator.NOT_STARTS_WITH,
        ].includes(selectedOperatorId) && (
          <ContainsStartsWithFilter
            autoFocus={autoFocus}
            fieldName={fieldName}
            currentFilterValues={currentFilterValues}
            onChange={handleOnValuesChange}
            disabled={disabled}
            searchIcon={searchIcon}
          />
        )}
        {filterType === "number" && (
          <NumberFilters
            autoFocus={autoFocus}
            currentFilterSchema={currentFilterSchema}
            selectedOperatorId={selectedOperatorId}
            inputValueOne={inputValue1}
            inputValueTwo={inputValue2}
            onInputOneChange={(val) => handleOnValuesChange([val, inputValue2])}
            onInputTwoChange={(val) => handleOnValuesChange([inputValue1, val])}
            onlyAllowPositiveIntegers={onlyAllowPositiveIntegers}
            disabled={disabled}
            fullWidth={fullWidth}
          />
        )}
        {filterType === "date" && (
          <DateFilters
            autoFocus={autoFocus}
            selectedOperatorId={selectedOperatorId}
            fieldName={fieldName}
            initialValues={currentFilterValues}
            onDateFromChange={(val) => handleOnValuesChange([val, inputValue2])}
            onDateToChange={(val) => handleOnValuesChange([inputValue1, val])}
            disabled={disabled}
            dataType={filterDataType}
            isInsideFilterBuilder={isInsideFilterBuilder}
          />
        )}
        {filterType === "duration" &&
          GT_LT_GTE_LTE_OPERATORS.includes(selectedOperatorId) && (
            <DurationFilter
              autoFocus={autoFocus}
              fieldName={fieldName}
              value={currentFilterValues.length ? currentFilterValues[0] : ""}
              disabled={disabled}
              onChange={(val) => handleOnValuesChange([val])}
            />
          )}
      </div>
    </>
  );
};

export default FilterTypeWrap;
